// Define validation schemas for each step of the form
export type ValidationError = {
  field: string;
  message: string;
};

export type ValidationResult = {
  isValid: boolean;
  errors: ValidationError[];
};

// Utility function to check if a value is empty
export const isEmpty = (value: any): boolean => {
  if (value === null || value === undefined) return true;
  if (typeof value === "string") return value.trim() === "";
  if (Array.isArray(value)) return value.length === 0;
  if (typeof value === "object") return Object.keys(value).length === 0;
  return false;
};

// Validate Step 1: Rate Your Experience
export const validateStep1 = (
  date: Date | null,
  ratings: Record<string, number>, // <-- ratings passed from the call
  starRatings: any[] = [], // <-- metadata about rating fields
  staffMemberDesc: string,
  textInputs: Record<string, string>,
  addOnQuestionAnswers: Record<string, string>,
  briefJson: any[],
  addOnQuestions: any[]
): ValidationResult => {
  const errors: ValidationError[] = [];

  // Validate date
  if (!date) {
    errors.push({ field: "date", message: "Date of visit is required" });
  }

  // Validate ratings
  if (!starRatings || starRatings.length === 0) {
    //errors.push({ field: "ratings", message: "At least one rating is required" });
  } else {
    let hasAtLeastOneRating = false;

    for (const ratingItem of starRatings) {
      const score = ratings?.[ratingItem.reportStarRatingId];

      if (score && score > 0) {
        hasAtLeastOneRating = true;
      } else {
        errors.push({
          field: `rating_${ratingItem.reportStarRatingId}`,
          message: `${
            ratingItem.starRatingDesc || "Rating"
          } must be greater than 0`,
        });
      }
    }

    if (!hasAtLeastOneRating) {
      errors.push({
        field: "ratings",
        message: "At least one rating is required",
      });
    }
  }

  // Validate staff member description
  // if (isEmpty(staffMemberDesc)) {
  //   errors.push({
  //     field: "staffMemberDesc",
  //     message: "Team member description is required",
  //   });
  // }

  // Validate text inputs for brief json
  briefJson?.forEach((item) => {
    const inputValue = textInputs[item.reportBriefJsonId];
    const wordCount =
      inputValue?.trim().split(/\s+/).filter(Boolean).length || 0;

    if (isEmpty(inputValue)) {
      errors.push({
        field: `textInput_${item.reportBriefJsonId}`,
        message: `${item.briefJsonTitle} is required`,
      });
    } else {
      if (item.minNoOfWords && wordCount < item.minNoOfWords) {
        errors.push({
          field: `textInput_${item.reportBriefJsonId}`,
          message: `${item.briefJsonTitle} must be at least ${item.minNoOfWords} words`,
        });
      }

      if (item.maxNoOfWords && wordCount > item.maxNoOfWords) {
        errors.push({
          field: `textInput_${item.reportBriefJsonId}`,
          message: `${item.briefJsonTitle} must be at most ${item.maxNoOfWords} words`,
        });
      }
    }
  });

  addOnQuestions?.forEach((item) => {
    const inputValue = addOnQuestionAnswers[item.reportAddOnQuestionId];

    if (isEmpty(inputValue)) {
      errors.push({
        field: `addOnQuestionField_${item.reportAddOnQuestionId}`,
        message: `${item.addOnQuestionText} is required`,
      });
    } 
  });

  return {
    isValid: errors.length === 0,
    errors,
  };
};

// Validate Step 2: Questions Bank
// export const validateStep2 = (
//   answers: Record<string, any>,
//   questionsData: any,
//   currentCategoryIndex: number,
// ): ValidationResult => {
//   const errors: ValidationError[] = []

//   if (!questionsData || !questionsData.questions) {
//     return { isValid: false, errors: [{ field: "general", message: "Questions data is missing" }] }
//   }

//   // Only validate questions from the current category
//   const currentCategory = questionsData.questions[currentCategoryIndex]
//   if (!currentCategory || !currentCategory.questions) {
//     return { isValid: true, errors: [] } // No questions to validate
//   }

//   // Get unique questions from the current category
//   //const uniqueQuestions = Array.from(new Map(currentCategory.questions.map((q: any) => [q.questionId, q])).values())
//   const uniqueQuestions = currentCategory.questions;

//   // Check if all required questions in the current category are answered
//   uniqueQuestions.forEach((question: any) => {
//     const answer = answers[question.questionId]
//     const isRequired = question.isRequired !== 0 // Assuming 0 means not required

//     if (isRequired) {
//       if (!answer) {
//         errors.push({
//           field: `question_${question.questionId}`,
//           message: `Question "${question.questions.replace(/<[^>]*>/g, "")}" must be answered`,
//         })
//       } else if (isEmpty(answer.value)) {
//         errors.push({
//           field: `question_${question.questionId}`,
//           message: `Question "${question.questions.replace(/<[^>]*>/g, "")}" must be answered`,
//         })
//       }
//     }
//   })

//   return {
//     isValid: errors.length === 0,
//     errors,
//   }
// }

export const validateStep2 = (
  answers: Record<string, any>,
  questionsData: any,
  currentCategoryIndex: number
): ValidationResult => {
  const errors: ValidationError[] = [];

  if (!questionsData || !questionsData.questions) {
    return {
      isValid: false,
      errors: [{ field: "general", message: "Questions data is missing" }],
    };
  }

  // Only validate questions from the current category
  const currentCategory = questionsData.questions[currentCategoryIndex];
  if (!currentCategory || !currentCategory.questions) {
    return { isValid: true, errors: [] }; // No questions to validate
  }

  // Get unique questions from the current category
  const uniqueQuestions = Array.from(
    new Map(
      currentCategory.questions.map((q: any) => [q.questionId, q])
    ).values()
  );

  //const uniqueQuestions = currentCategory.
  // questions

  // Check if all required questions in the current category are answered
  uniqueQuestions.forEach((question: any) => {
    const answer = answers[question.questionId];
    const isRequired = question.isRequired !== 0; // Assuming 0 means not required

    if (isRequired) {
      if (!answer) {
        errors.push({
          field: `question_${question.questionId}`,
          message: `Question "${question.questions.replace(
            /<[^>]*>/g,
            ""
          )}" must be answered`,
        });
      } else if (isEmpty(answer.value)) {
        errors.push({
          field: `question_${question.questionId}`,
          message: `Question "${question.questions.replace(
            /<[^>]*>/g,
            ""
          )}" must be answered`,
        });
      }
    }

    // Validate image requirements if an answer is provided
    if (answer && answer.value) {
      try {
        // Get the selected option to check if it requires images
        const options = JSON.parse(question.qOptionJson || "[]");
        const selectedOption = options.find(
          (opt: any) => opt.value === answer.value
        );

        if (selectedOption && selectedOption.optionImage > 0) {
          // Check if the required number of images are uploaded
          const uploadedImages = answer.photoUrl
            ? answer.photoUrl.split(",").filter(Boolean).length
            : 0;

          if (uploadedImages < selectedOption.optionImage) {
            errors.push({
              field: `question_${question.questionId}_images`,
              message: `This answer requires minimum ${
                selectedOption.optionImage
              } image${
                selectedOption.optionImage > 1 ? "s" : ""
              }. `,
            });
          }
        }

        // ✅ Validate minChar for comment
        if (selectedOption?.minChar === 0) {
          return;
        }
        if ((answer.comment || "").trim().length < selectedOption.minChar) {
          errors.push({
            field: `question_${question.questionId}_comment`,
            message: `Minimum ${selectedOption.minChar} characters required in comment.`,
          });
        }
      } catch (error) {
        console.error("Error parsing options JSON:", error);
      }
    }
  });

  return {
    isValid: errors.length === 0,
    errors,
  };
};

// Validate Step 3: Other Details
export const validateStep3 = (
  invoice: { food: number; bar: number; gaming: number; total: number },
  uploadedFiles: Record<string, any[]>
): ValidationResult => {
  const errors: ValidationError[] = [];

  // Validate invoice total
  if (invoice.total <= 0) {
    errors.push({
      field: "invoice_total",
      message: "Total invoice amount must be greater than 0",
    });
  }

  // Validate required files
  if (!uploadedFiles.taskPhotos || uploadedFiles.taskPhotos.length === 0) {
    errors.push({
      field: "taskPhotos",
      message: "At least one task photo is required",
    });
  }

  if (
    !uploadedFiles.invoiceReceipt ||
    uploadedFiles.invoiceReceipt.length === 0
  ) {
    errors.push({
      field: "invoiceReceipt",
      message: "At least one invoice/receipt photo is required",
    });
  }

  if (!uploadedFiles.checkInFile || uploadedFiles.checkInFile.length === 0) {
    errors.push({
      field: "checkInFile",
      message: "Check-in photo is required",
    });
  } else if (uploadedFiles.checkInFile.length > 1) {
    errors.push({
      field: "checkInFile",
      message: "Only 1 Image can be upload For Check In File",
    });
  }

  if (!uploadedFiles.checkOutFile || uploadedFiles.checkOutFile.length === 0) {
    errors.push({
      field: "checkOutFile",
      message: "Check-out photo is required",
    });
  } else if (uploadedFiles.checkOutFile.length > 1) {
    errors.push({
      field: "checkOutFile",
      message: "Only 1 Image can be upload For Check Out File",
    });
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
};
